/**
 * KB-3319 — build the Partial<data> patch a Restore passes to the update service (updateOffer /
 * updateCampLocation / updateCampOrganization).
 *
 * The update services merge at the TOP LEVEL only (`{ ...current, ...patch }`, then a full-doc write),
 * and Firestore set/merge never splits a dotted key. So a nested or array restore cannot be expressed
 * as `{ "a.b": value }`; it must reconstruct the whole top-level key from the current document, with the
 * one changed leaf swapped in and every sibling preserved. A simple top-level scalar needs no read.
 *
 * Three special leaves (per fieldPathLib and Ruling 1):
 *  - `/variations/[key=k]` — the value is the whole variation element; replace the array member whose
 *    `key === k` (append if absent), write the array back.
 *  - `/variationData/timings/afterCareWindow` — a SYNTHETIC leaf (KB-2815); its value is the pair
 *    `{ afterCareStart, afterCareEnd }`, which maps onto those two real fields, not onto a field named
 *    `afterCareWindow`.
 *  - `/variationData/timings/dropOffPickUpWindow` — KB-4552's synthetic leaf for KB-4535's stated window; its
 *    value names the drop-off/pick-up `timings` keys (an agent apply records it). Each named key is restored;
 *    a null one is removed, as the offer never held it.
 */
import { SESSION_WINDOW_DEFAULTS } from "@/functions-shared/offer/sessionWindowRule";
import { withCurrentDoNotContact } from "@/services/outreachUnsubscribe";

const AFTER_CARE_WINDOW = "afterCareWindow";
const DROP_OFF_PICK_UP_WINDOW = "dropOffPickUpWindow";
/** The window's keys, from the ONE window rule (KB-3613), not re-listed. */
const DROP_OFF_PICK_UP_KEYS: ReadonlySet<string> = new Set(
  Object.values(SESSION_WINDOW_DEFAULTS as Record<string, { keys: ReadonlyArray<string> }>).flatMap((g) => g.keys),
);

/** `[key=k]` array selector → the key `k`, else null. */
function keySelector(seg: string): string | null {
  const m = /^\[key=(.+)\]$/.exec(seg);
  return m ? m[1] : null;
}

function cloneDoc<T>(v: T): T {
  return JSON.parse(JSON.stringify(v ?? null));
}

/** Set the leaf named by `segs` (walked from `node`) to `value`, mutating `node` in place. */
function setDeep(node: Record<string, unknown> | unknown[], segs: string[], value: unknown): void {
  const seg = segs[0];
  const isLeaf = segs.length === 1;
  const sel = keySelector(seg);

  if (sel != null) {
    // Array-by-key selector.
    if (!Array.isArray(node)) throw new Error(`expected an array at [key=${sel}]`);
    let idx = node.findIndex((el) => el && typeof el === "object" && (el as Record<string, unknown>).key === sel);
    if (idx < 0) {
      node.push({ key: sel });
      idx = node.length - 1;
    }
    if (isLeaf) {
      node[idx] = value;
      return;
    }
    setDeep(node[idx] as Record<string, unknown>, segs.slice(1), value);
    return;
  }

  const obj = node as Record<string, unknown>;

  if (isLeaf) {
    if (seg === AFTER_CARE_WINDOW) {
      // Synthetic pair — expand onto the two real fields, never a literal `afterCareWindow`.
      const pair = (value ?? {}) as Record<string, unknown>;
      obj.afterCareStart = pair.afterCareStart ?? null;
      obj.afterCareEnd = pair.afterCareEnd ?? null;
      return;
    }
    if (seg === DROP_OFF_PICK_UP_WINDOW) {
      // Synthetic window — expand onto the real drop-off/pick-up keys, never a literal `dropOffPickUpWindow`. A null
      // value is a window the offer did not hold (a revert row of an apply that created it): every key is removed.
      if (value == null) {
        for (const k of DROP_OFF_PICK_UP_KEYS) delete obj[k];
        return;
      }
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        if (!DROP_OFF_PICK_UP_KEYS.has(k)) continue;
        // The recorded value is null for absent (an eight-key snapshot): an explicit null the offer holds stays.
        if (v == null) {
          if (obj[k] !== null) delete obj[k];
        } else obj[k] = v;
      }
      return;
    }
    obj[seg] = value;
    return;
  }

  if (obj[seg] == null || typeof obj[seg] !== "object") obj[seg] = {};
  setDeep(obj[seg] as Record<string, unknown>, segs.slice(1), value);
}

/**
 * Returns a top-level-keyed patch. For a nested/array path, `currentDoc` is required (its top-level key
 * is reconstructed with the one leaf changed); a simple scalar path ignores it.
 */
export function buildRestorePatch(
  fieldPath: string,
  value: unknown,
  currentDoc: Record<string, unknown> | null,
): Record<string, unknown> {
  const segs = fieldPath.split("/").filter(Boolean);
  if (segs.length === 0) throw new Error("empty field path");

  // A single, non-selector segment is a plain top-level field — no read needed.
  if (segs.length === 1 && keySelector(segs[0]) == null) {
    return { [segs[0]]: value };
  }

  if (!currentDoc) throw new Error("current document required to restore a nested or array field");
  const clone = cloneDoc(currentDoc);

  // Seed the top-level container so setDeep can descend even if the current doc lacks it.
  const top = segs[0];
  const topSel = keySelector(top);
  if (topSel != null) {
    throw new Error("a field path cannot begin with an array selector");
  }
  if (clone[top] == null) clone[top] = segs.length > 1 && keySelector(segs[1]) != null ? [] : {};

  setDeep(clone, segs, value);
  return { [top]: clone[top] };
}

/** An org restore of `contacts` or anything under it. Those are read and written in one transaction. */
export function isOrgContactsRestore(kind: "offer" | "campLocation" | "campOrganization", fieldPath: string): boolean {
  return kind === "campOrganization" && fieldPath.split("/").filter(Boolean)[0] === "contacts";
}

/**
 * The `contacts` an org restore writes: the restored contacts, with contacts[0]'s do-not-contact fields
 * taken from `currentDoc` (the org read in the restore's transaction), so a restore never sets, clears
 * or back-dates the flag, even a restore of `contacts/0/doNotContact`. The flag changes only through
 * setOrgDoNotContact and the director's unsubscribe link. Null when the restored value is null (the
 * update service skips it, leaving contacts as they are).
 */
export function restoreOrgContacts(
  fieldPath: string,
  value: unknown,
  currentDoc: Record<string, unknown>,
): unknown[] | null {
  const restored = buildRestorePatch(fieldPath, value, currentDoc).contacts;
  if (restored == null) return null;
  if (!Array.isArray(restored)) throw new Error("a restored contacts value must be a list");
  return withCurrentDoNotContact(restored, currentDoc.contacts);
}

/** Firestore collection an entity kind lives in. */
export function restoreCollectionFor(kind: "offer" | "campLocation" | "campOrganization"): string {
  return kind === "offer" ? "offers" : kind === "campLocation" ? "campLocations" : "campOrganizations";
}
